import posthog from 'posthog-js'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'

import { getCurrentRole, getIsOwnerTenant } from '@/shared/auth/store'
import { hasRequiredRole } from '@/shared/auth/utils/user'

import { ONBOARDING_TASKS } from './task-registry'

import type {
	OnboardingProgress,
	OnboardingTask,
	OnboardingTaskId,
} from './types'
import type { Role } from '@/shared/auth/types'

const STORE_NAME = '@bitlyft/onboarding'

/**
 * Create default onboarding progress filtered by user role
 */
export function createDefaultOnboardingState(
	userRole?: Role | null,
): OnboardingProgress {
	const role = userRole ?? getCurrentRole()

	// No role = empty state
	if (!role) {
		return {
			progress: 0,
			quickStart: { completed: false, completedAt: null },
			gettingStarted: {} as Record<OnboardingTaskId, OnboardingTask>,
		}
	}

	// Filter tasks by role
	const filteredTasks = Object.entries(ONBOARDING_TASKS).filter(([_, task]) =>
		hasRequiredRole(role, task.roleRequired),
	)

	return {
		progress: 0,
		quickStart: { completed: false, completedAt: null },
		gettingStarted: Object.fromEntries(filteredTasks) as Record<
			OnboardingTaskId,
			OnboardingTask
		>,
	}
}

/**
 * Calculate progress percentage from tasks
 */
function calculateProgress(
	tasks: Record<OnboardingTaskId, OnboardingTask>,
): number {
	const total = Object.keys(tasks).length
	if (total === 0) return 0

	const done = Object.values(tasks).filter(
		(task) => task.completed || task.skipped,
	).length

	return Math.round((done / total) * 100)
}

// Store state type
type OnboardingState = {
	progress: OnboardingProgress | null
	isLoading: boolean
}

// Store actions type
type OnboardingActions = {
	initialize: (
		clerkProgress: OnboardingProgress | null,
		role: Role | null,
	) => void
	markTaskComplete: (taskId: OnboardingTaskId) => void
	markTaskAsSkipped: (taskId: OnboardingTaskId) => void
	reset: () => void
}

type OnboardingStore = OnboardingState & OnboardingActions

/**
 * Single Zustand store for onboarding state
 * Accessible from both React components and non-React code
 */
export const useOnboardingStore = create<OnboardingStore>()(
	devtools(
		(set, get) => ({
			progress: null,
			isLoading: true,

			/**
			 * Initialize store from Clerk metadata or create default
			 */
			initialize: (clerkProgress, role) => {
				const progress = clerkProgress ?? createDefaultOnboardingState(role)
				set({ progress, isLoading: false }, false, 'onboarding/initialize')
			},

			/**
			 * Mark a task as complete
			 * Guards: owner tenant only, not already completed
			 */
			markTaskComplete: (taskId) => {
				if (!getIsOwnerTenant()) return

				const { progress } = get()
				if (!progress) return

				const task = progress.gettingStarted[taskId]
				if (!task || task.completed) {
					posthog.capture('onboarding_redundant_completion', { taskId })
					return
				}

				const completedAt = new Date().toISOString()

				set(
					(state) => {
						if (!state.progress) return state

						const updatedTasks = {
							...state.progress.gettingStarted,
							[taskId]: {
								...state.progress.gettingStarted[taskId],
								completed: true,
								completedAt,
							},
						}

						const progressPercent = calculateProgress(updatedTasks)
						const allDone = progressPercent === 100

						return {
							progress: {
								...state.progress,
								progress: progressPercent,
								gettingStarted: updatedTasks,
								quickStart: allDone
									? { completed: true, completedAt }
									: state.progress.quickStart,
							},
						}
					},
					false,
					'onboarding/markTaskComplete',
				)

				posthog.capture('onboarding_task_completed', {
					taskId,
					completedAt,
					completionSource: 'direct_usage',
				})
			},

			/**
			 * Mark a task as skipped
			 * Guards: owner tenant only, not already completed/skipped
			 */
			markTaskAsSkipped: (taskId) => {
				if (!getIsOwnerTenant()) return

				const { progress } = get()
				if (!progress) return

				const task = progress.gettingStarted[taskId]
				if (!task || task.completed || task.skipped) {
					posthog.capture('onboarding_redundant_skip', { taskId })
					return
				}

				const skippedAt = new Date().toISOString()

				set(
					(state) => {
						if (!state.progress) return state

						const updatedTasks = {
							...state.progress.gettingStarted,
							[taskId]: {
								...state.progress.gettingStarted[taskId],
								skipped: true,
								skippedAt,
							},
						}

						const progressPercent = calculateProgress(updatedTasks)
						const allDone = progressPercent === 100

						return {
							progress: {
								...state.progress,
								progress: progressPercent,
								gettingStarted: updatedTasks,
								quickStart: allDone
									? { completed: true, completedAt: skippedAt }
									: state.progress.quickStart,
							},
						}
					},
					false,
					'onboarding/markTaskAsSkipped',
				)

				posthog.capture('onboarding_task_skipped', { taskId, skippedAt })
			},

			/**
			 * Reset to initial loading state
			 */
			reset: () => {
				set({ progress: null, isLoading: true }, false, 'onboarding/reset')
			},
		}),
		{ name: STORE_NAME },
	),
)

// ============================================================================
// React hooks for components
// ============================================================================

/**
 * Get onboarding actions
 */
export const useOnboardingActions = () =>
	useOnboardingStore(
		useShallow((state) => ({
			initialize: state.initialize,
			markTaskComplete: state.markTaskComplete,
			markTaskAsSkipped: state.markTaskAsSkipped,
			reset: state.reset,
		})),
	)

/**
 * Get a specific task by ID
 */
export const useOnboardingTask = (taskId: OnboardingTaskId) =>
	useOnboardingStore((state) => state.progress?.gettingStarted[taskId])

/**
 * Get progress percentage
 */
export const useOnboardingProgressPercentage = () =>
	useOnboardingStore((state) => state.progress?.progress ?? 0)

/**
 * Get all getting started tasks
 */
export const useGettingStartedTasks = () =>
	useOnboardingStore((state) => state.progress?.gettingStarted)
